/**
 * ImportTabs - Tab switcher for Upload File / Paste Text modes
 *
 * Provides consistent tab UI across all import modals.
 */
import type { ReactElement, ReactNode } from 'react';
import { FaCloudUploadAlt, FaPaste } from 'react-icons/fa';

import type { TabItem } from '@/components/Tabs/Tabs';
import Tabs from '@/components/Tabs/Tabs';

import type { ImportTabId } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface ImportTabsProps {
  /** Currently active tab */
  activeTab: ImportTabId;
  /** Callback when tab changes */
  onTabChange: (tab: ImportTabId) => void;
  /** Content to render for the upload tab */
  uploadContent: ReactNode;
  /** Content to render for the paste tab */
  pasteContent: ReactNode;
}

// ============================================================================
// Component
// ============================================================================

const IMPORT_TABS: TabItem<ImportTabId>[] = [
  { id: 'upload', label: 'Upload File', icon: <FaCloudUploadAlt className="h-4 w-4" /> },
  { id: 'paste', label: 'Paste Text', icon: <FaPaste className="h-4 w-4" /> },
];

export default function ImportTabs({ activeTab, onTabChange, uploadContent, pasteContent }: ImportTabsProps): ReactElement {
  return (
    <div className="space-y-4">
      <Tabs tabs={IMPORT_TABS} activeId={activeTab} onChange={onTabChange} label="Import source" />

      {/* Tab content */}
      <div>{activeTab === 'upload' ? uploadContent : pasteContent}</div>
    </div>
  );
}
